import './tab-claims.scss';

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { PaperLayout } from '../../../presentation';
import { ClaimEntityTable } from './claim-entity-grid';
import { TabPanel } from './components/tab-panel';
import { useAuthContext } from '../../auth/require-auth';
import { useGetUsers } from '@converge/graphql';

const STORAGE_KEY = 'claims-tab-selected';
const TAB_PARAMS = {
  DIARY: 'diary',
  ALL: 'all',
} as const;

const TAB_INDICES = {
  [TAB_PARAMS.DIARY]: 0,
  [TAB_PARAMS.ALL]: 1,
} as const;

export const TabClaims = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthContext();
  const { data } = useGetUsers({ username: user?.user.username || '' });

  // Get initial tab state from URL params and localStorage
  const getInitialTabIndex = () => {
    const urlParams = new URLSearchParams(location.search);
    const activeTab = urlParams.get('tab');

    // If URL param exists, use it
    if (activeTab && activeTab in TAB_INDICES) {
      return TAB_INDICES[activeTab as keyof typeof TAB_INDICES];
    }

    // Check localStorage
    const savedTab = localStorage.getItem(STORAGE_KEY);
    if (savedTab && savedTab in TAB_INDICES) {
      return TAB_INDICES[savedTab as keyof typeof TAB_INDICES];
    }

    // Default to first tab
    return 0;
  };

  const [activeTab, setActiveTab] = useState<number>(getInitialTabIndex);

  // Get current user info
  const currentUserInfo = data?.users?.edges?.filter(
    (currentUser) => currentUser.node.username === user?.user.username
  );

  // Get first name if available, otherwise use default text
  const userFirstName = currentUserInfo?.[0]?.node?.firstName || '';

  // Map tab index to param value
  const getTabParam = (tabIndex: number): string => {
    const paramMap = {
      0: TAB_PARAMS.DIARY,
      1: TAB_PARAMS.ALL,
    };
    return paramMap[tabIndex as keyof typeof paramMap] || TAB_PARAMS.DIARY;
  };

  // Update URL and localStorage when tab changes
  const updateTabState = useCallback(
    (tabIndex: number) => {
      const tabParam = getTabParam(tabIndex);

      // Save to localStorage
      localStorage.setItem(STORAGE_KEY, tabParam);

      // Update URL
      const urlParams = new URLSearchParams(location.search);
      urlParams.set('tab', tabParam);
      const newUrl = `${location.pathname}?${urlParams.toString()}`;
      navigate(newUrl, { replace: true });
    },
    [navigate, location.pathname, location.search]
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    updateTabState(newValue);
  };

  // Sync URL on component mount if needed
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const currentTabParam = urlParams.get('tab');
    const expectedTabParam = getTabParam(activeTab);

    // If URL doesn't match current tab, update it
    if (!currentTabParam || currentTabParam !== expectedTabParam) {
      updateTabState(activeTab);
    }
  }, [activeTab, location.pathname, location.search, updateTabState]);

  return (
    <PaperLayout>
      <Box
        className="claims-container"
        sx={{
          p: 3,
          boxSizing: 'border-box',
          position: 'relative',
          width: '100%',
          overflow: 'auto',
        }}
      >
        {/* Tab navigation */}
        <Box
          className="tab-header"
          sx={{
            position: 'sticky',
            top: '10px',
            zIndex: 12,
            paddingTop: '10px',
            paddingBottom: '10px',
            marginBottom: '20px',
            width: '100%',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="claims tabs"
            sx={{
              fontWeight: 'bold',
              color: '#000000 !important',
              borderBottom: '1px solid #000000',
              backgroundColor: 'white',
              marginBottom: '10px',
              '& .Mui-selected': {
                fontWeight: 'bold',
              },
            }}
            variant="fullWidth"
          >
            <Tab
              sx={{
                textTransform: 'none',
                fontSize: '16px',
                color: '#000000 !important',
              }}
              label={`${
                userFirstName ? `${userFirstName}'s` : 'My'
              } Claim Diary`}
              id="claims-tab-0"
            />
            <Tab
              sx={{
                textTransform: 'none',
                fontSize: '16px',
                color: '#000000 !important',
              }}
              label="All Claims"
              id="claims-tab-1"
            />
          </Tabs>
        </Box>

        {/* Tab content */}
        <TabPanel value={activeTab} index={0}>
          <Box>
            <ClaimEntityTable isAllClaims={false} />
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box>
            <ClaimEntityTable isAllClaims={true} />
          </Box>
        </TabPanel>
      </Box>
      <Box className="claim-btn-container">
        {/* <Link to="/claims/create-claim" className="new-claim-btn">
          <NewConvergeButton label={'START NEW CLAIM'} variant="contained" />
        </Link> */}
      </Box>
    </PaperLayout>
  );
};

export default TabClaims;
